/**
 * Briefs the evaluator persona proposed and Chuck has not yet saved or discarded.
 *
 * Fed by the `writing.proposeBrief` UI intent (registerWritingBoardIntents) and
 * read by the card's BriefPanel. One pending proposal per card: a newer one
 * replaces it, as the persona re-proposes the whole brief when answers change.
 * Client state only, so a reload drops an unsaved proposal; asking again
 * re-proposes it (docs/plans/2026-10-13-writing-evaluate-redesign.md, decision 3).
 */
import { create } from 'zustand';

import { registerUserScopedReset } from '@shared/services/userScopedState';

import type { Brief } from '../types';

interface BriefProposalState {
  proposalsByCard: Record<string, Brief>;
  /** Record (or replace) the card's pending proposal. */
  propose: (cardId: string, brief: Brief) => void;
  /** Drop the card's pending proposal (saved or discarded). */
  clear: (cardId: string) => void;
}

export const useBriefProposalStore = create<BriefProposalState>(set => ({
  proposalsByCard: {},
  propose: (cardId, brief) =>
    set(s => ({ proposalsByCard: { ...s.proposalsByCard, [cardId]: brief } })),
  clear: cardId =>
    set(s => {
      if (!(cardId in s.proposalsByCard)) return s;
      const next = { ...s.proposalsByCard };
      delete next[cardId];
      return { proposalsByCard: next };
    }),
}));

registerUserScopedReset('brief-proposals', () =>
  useBriefProposalStore.setState({ proposalsByCard: {} })
);
